/**
 * A aba "Documentos" do cliente (#811, clínica): identidade, carteirinha,
 * termos e contratos do paciente.
 *
 * ── Uma aba, e não um bloco do cadastro (16/10/2026) ─────────────────────
 *
 * Os documentos eram o terceiro bloco da clínica dentro de "Cadastro", em
 * linhas de texto. Viraram aba própria, com a MESMA forma dos documentos da
 * oportunidade: a área de soltar, a fila antes do envio e a grade com prévia
 * (`shared/ui/documentos`). Quem aprendeu uma tela já sabe usar a outra.
 *
 * ── O que é só do paciente ───────────────────────────────────────────────
 *
 *   · o filtro por tipo, com a contagem de cada um;
 *   · o tipo e a validade valem para o envio inteiro — a frente e o verso da
 *     carteirinha vão juntos, como a recepção trabalha;
 *   · o selo de "Vencido" e "Vence em breve" (30 dias) embaixo do cartão.
 *
 * ── O link de abrir vence ────────────────────────────────────────────────
 *
 * Cada documento vem com um link assinado que vale cinco minutos, e ele é
 * também a fonte da miniatura. A lista é relida a cada quatro minutos.
 */

import { useCallback, useState, type DragEvent, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  LIMITES_DO_DOCUMENTO_DO_PACIENTE,
  type CategoriaDoDocumentoDoPaciente,
  type DocumentoDoPaciente,
} from '@cliqcrm/contracts';
import { useApi, useDocumentosDoPaciente } from '@/shared/api/consultas';
import { chaves } from '@/shared/api/chaves';
import { recadoDeErro } from '@/shared/api/recado-de-erro';
import { Botao, Campo, CampoSelecao, Chip, Dialogo, FiltroPilula, Icone } from '@/shared/ui';
import { Carregando, Erro } from '@/shared/ui/estados/estados';
import { Faixa } from '@/shared/ui/estados/faixa';
import {
  VisualizadorDeMidia,
  type MidiaParaVer,
} from '@/shared/ui/visualizador-de-midia/visualizador-de-midia';
import {
  dataPorExtenso,
  estadoDaValidade,
  NOMES_DA_CATEGORIA,
  separarPorTamanho,
  tamanhoLegivel,
} from '../lib/documentos-do-paciente';
import { hojeLocal } from '../lib/ficha-do-paciente';
import '@/shared/ui/documentos/documentos.css';
import './aba-dos-documentos.css';

type Filtro = CategoriaDoDocumentoDoPaciente | 'todos';

/** A prévia que a grade desenha: a própria foto, ou o ícone do tipo. */
type TipoNaTela = 'foto' | 'pdf' | 'arquivo';

function tipoDo(documento: DocumentoDoPaciente): TipoNaTela {
  if (documento.mime.startsWith('image/')) return 'foto';
  if (documento.extensao?.toLowerCase() === 'pdf' || documento.mime === 'application/pdf') {
    return 'pdf';
  }
  return 'arquivo';
}

function quando(instante: string): string {
  return new Date(instante).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function arquivosNoPlural(n: number): string {
  return n === 1 ? '1 arquivo' : `${n} arquivos`;
}

export function DocumentosNoCliente({
  clienteId,
  podeEditar,
}: {
  clienteId: string;
  podeEditar: boolean;
}): ReactNode {
  const consulta = useDocumentosDoPaciente(clienteId);
  const [filtro, definirFiltro] = useState<Filtro>('todos');
  const [apagando, definirApagando] = useState<DocumentoDoPaciente | null>(null);
  const [vendo, definirVendo] = useState<MidiaParaVer | null>(null);
  const fecharVisualizador = useCallback(() => definirVendo(null), []);

  if (consulta.isPending) {
    return <Carregando forma="lista" linhas={2} rotulo="Carregando os documentos" />;
  }
  if (consulta.isError) {
    return (
      <Erro
        erro={consulta.error}
        titulo="Não foi possível abrir os documentos"
        aoRepetir={() => void consulta.refetch()}
      />
    );
  }

  const documentos = consulta.data.itens;
  const hoje = hojeLocal();
  const categorias = (Object.keys(NOMES_DA_CATEGORIA) as CategoriaDoDocumentoDoPaciente[])
    .map((categoria) => ({
      categoria,
      quantos: documentos.filter((d) => d.categoria === categoria).length,
    }))
    .filter(({ quantos }) => quantos > 0);
  const vencendo = documentos.filter(
    (d) => estadoDaValidade(d.validade, hoje) === 'vence-em-breve',
  ).length;
  const vencidos = documentos.filter(
    (d) => estadoDaValidade(d.validade, hoje) === 'vencido',
  ).length;
  const visiveis =
    filtro === 'todos' ? documentos : documentos.filter((d) => d.categoria === filtro);

  return (
    <div className="docs-cli">
      {podeEditar && <EnvioDeDocumentos clienteId={clienteId} />}

      {documentos.length === 0 ? (
        <p className="docs__vazio">
          {podeEditar
            ? 'Nenhum documento enviado ainda. Envie o primeiro pela área acima.'
            : 'Nenhum documento enviado ainda.'}
        </p>
      ) : (
        <>
          <div className="docs-cli__barra">
            <div className="docs-cli__filtros" role="group" aria-label="Filtrar por tipo">
              <FiltroPilula
                ligado={filtro === 'todos'}
                aoAlternar={() => definirFiltro('todos')}
                contagem={documentos.length}
              >
                Todos
              </FiltroPilula>
              {categorias.map(({ categoria, quantos }) => (
                <FiltroPilula
                  key={categoria}
                  ligado={filtro === categoria}
                  aoAlternar={() => definirFiltro(filtro === categoria ? 'todos' : categoria)}
                  contagem={quantos}
                >
                  {NOMES_DA_CATEGORIA[categoria]}
                </FiltroPilula>
              ))}
            </div>
            <span className="docs-cli__resumo">
              {[
                arquivosNoPlural(documentos.length),
                vencidos > 0 ? `${vencidos} ${vencidos === 1 ? 'vencido' : 'vencidos'}` : null,
                vencendo > 0 ? `${vencendo} vence${vencendo === 1 ? '' : 'm'} em breve` : null,
              ]
                .filter((parte): parte is string => parte !== null)
                .join(' · ')}
            </span>
          </div>

          <ul className="docs__grade" aria-label="Documentos do paciente">
            {visiveis.map((documento) => (
              <CartaoDoDocumento
                key={documento.id}
                documento={documento}
                hoje={hoje}
                podeEditar={podeEditar}
                aoVer={() =>
                  definirVendo({ tipo: 'imagem', url: documento.url, nome: documento.nome })
                }
                aoApagar={() => definirApagando(documento)}
              />
            ))}
          </ul>
        </>
      )}

      {apagando && (
        <ApagarDocumento
          clienteId={clienteId}
          documento={apagando}
          aoFechar={() => definirApagando(null)}
        />
      )}
      {vendo && <VisualizadorDeMidia midia={vendo} aoFechar={fecharVisualizador} />}
    </div>
  );
}

function CartaoDoDocumento({
  documento,
  hoje,
  podeEditar,
  aoVer,
  aoApagar,
}: {
  documento: DocumentoDoPaciente;
  hoje: string;
  podeEditar: boolean;
  aoVer: () => void;
  aoApagar: () => void;
}): ReactNode {
  const tipo = tipoDo(documento);
  const validade = estadoDaValidade(documento.validade, hoje);
  // Foto abre no visualizador, como na oportunidade; PDF e o resto, em aba nova.
  const comoAbrir =
    tipo === 'foto'
      ? {
          onClick: (evento: { preventDefault: () => void }) => {
            evento.preventDefault();
            aoVer();
          },
        }
      : {};

  return (
    <li className="doc">
      <div className="doc__previa">
        <a
          className="doc__ver"
          href={documento.url}
          target="_blank"
          rel="noopener noreferrer"
          tabIndex={-1}
          aria-hidden
          {...comoAbrir}
        >
          {tipo === 'foto' ? (
            <img src={documento.url} alt="" loading="lazy" />
          ) : (
            <span className={`doc__icone doc__icone--${tipo}`}>
              <Icone nome={tipo === 'pdf' ? 'documento' : 'arquivo'} />
              {tipo === 'pdf' && <span>PDF</span>}
            </span>
          )}
        </a>
        <span className="docs-cli__categoria">
          <Chip>{NOMES_DA_CATEGORIA[documento.categoria]}</Chip>
        </span>
        <span className="doc__acoes">
          <a
            className="doc__acao"
            href={documento.url}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Ver ${documento.nome}`}
            {...comoAbrir}
          >
            <Icone nome="olho" />
          </a>
          {podeEditar && (
            <button
              type="button"
              className="doc__acao doc__acao--apagar"
              aria-label={`Apagar ${documento.nome}`}
              onClick={aoApagar}
            >
              <Icone nome="lixeira" />
            </button>
          )}
        </span>
      </div>
      <a
        className="doc__nome docs-cli__nome"
        href={documento.url}
        target="_blank"
        rel="noopener noreferrer"
        title={documento.nome}
        aria-label={`Abrir ${documento.nome}`}
        {...comoAbrir}
      >
        {documento.nome}
      </a>
      <span className="doc__meta">
        {validade === 'vencido' && <Chip tom="erro">Vencido</Chip>}
        {validade === 'vence-em-breve' && <Chip tom="atencao">Vence em breve</Chip>}
        {documento.validade && (
          <span>
            {validade === 'vencido' ? 'valia' : 'vale'} até {dataPorExtenso(documento.validade)}
          </span>
        )}
        {!documento.validade && (
          <time dateTime={documento.enviado_em}>{quando(documento.enviado_em)}</time>
        )}
        <span>{tamanhoLegivel(documento.tamanho)}</span>
      </span>
    </li>
  );
}

/** A área de soltar, e a fila com o tipo e a validade do envio. */
function EnvioDeDocumentos({ clienteId }: { clienteId: string }): ReactNode {
  const api = useApi();
  const cache = useQueryClient();
  const [arquivos, definirArquivos] = useState<File[]>([]);
  const [recusados, definirRecusados] = useState<string[]>([]);
  const [arrastando, definirArrastando] = useState(false);
  const [categoria, definirCategoria] = useState<CategoriaDoDocumentoDoPaciente>('identidade');
  const [validade, definirValidade] = useState('');

  const limpar = () => {
    definirArquivos([]);
    definirRecusados([]);
    definirValidade('');
  };

  const acrescentar = (lista: FileList | null) => {
    const { aceitos, recusados: fora } = separarPorTamanho([
      ...arquivos,
      ...Array.from(lista ?? []),
    ]);
    definirArquivos(aceitos);
    definirRecusados(fora);
  };

  const enviar = useMutation({
    mutationFn: () => {
      if (!api) throw new Error('Sem sessão.');
      const formulario = new FormData();
      for (const arquivo of arquivos) formulario.append('arquivos', arquivo, arquivo.name);
      formulario.append('categoria', categoria);
      if (validade !== '') formulario.append('validade', validade);
      return api.enviarDocumentosDoPaciente({ caminho: { id: clienteId }, corpo: formulario });
    },
    onSuccess: async () => {
      limpar();
      await cache.invalidateQueries({ queryKey: chaves.documentosDoPaciente(clienteId) });
    },
  });

  const aoArrastar = (evento: DragEvent<HTMLLabelElement>, dentro: boolean) => {
    evento.preventDefault();
    evento.stopPropagation();
    definirArrastando(dentro);
  };

  return (
    <>
      <label
        className={arrastando ? 'soltar soltar--arrastando' : 'soltar'}
        onDragEnter={(e) => aoArrastar(e, true)}
        onDragOver={(e) => aoArrastar(e, true)}
        onDragLeave={(e) => aoArrastar(e, false)}
        onDrop={(e) => {
          aoArrastar(e, false);
          acrescentar(e.dataTransfer.files);
        }}
      >
        <span className="soltar__icone">
          <Icone nome="enviar" />
        </span>
        <span className="soltar__texto">
          <span>
            Arraste arquivos aqui ou <span className="soltar__escolher">escolha no computador</span>
          </span>
          <small>
            PDF ou fotos, até {LIMITES_DO_DOCUMENTO_DO_PACIENTE.arquivosPorEnvio} arquivos por
            envio, de até {LIMITES_DO_DOCUMENTO_DO_PACIENTE.megabytesPorArquivo} MB cada
          </small>
        </span>
        <input
          className="soltar__campo"
          type="file"
          multiple
          aria-label="Escolher documentos do paciente"
          onChange={(e) => {
            acrescentar(e.currentTarget.files);
            e.currentTarget.value = '';
          }}
        />
      </label>

      {recusados.length > 0 && (
        <Faixa tom="atencao">
          Ficaram de fora, por estarem vazios, passarem de{' '}
          {LIMITES_DO_DOCUMENTO_DO_PACIENTE.megabytesPorArquivo} MB ou de{' '}
          {LIMITES_DO_DOCUMENTO_DO_PACIENTE.arquivosPorEnvio} arquivos: {recusados.join(', ')}.
        </Faixa>
      )}

      {arquivos.length > 0 && (
        <form
          className="docs-cli__envio"
          aria-label="Arquivos para enviar"
          onSubmit={(evento) => {
            evento.preventDefault();
            enviar.mutate();
          }}
        >
          <ul className="fila-docs__itens" aria-label="Arquivos para enviar">
            {arquivos.map((arquivo, indice) => (
              <li key={`${indice}-${arquivo.name}`} className="fila-docs__item">
                <span className="fila-docs__mini" aria-hidden>
                  <Icone nome={arquivo.type === 'application/pdf' ? 'documento' : 'arquivo'} />
                </span>
                <span className="fila-docs__nome" title={arquivo.name}>
                  {arquivo.name}
                </span>
                <button
                  type="button"
                  className="fila-docs__tirar"
                  aria-label={`Tirar ${arquivo.name} da fila`}
                  onClick={() => definirArquivos(arquivos.filter((_, i) => i !== indice))}
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
          <div className="docs-cli__campos">
            <CampoSelecao
              id="documentos-categoria"
              rotulo="Tipo de documento"
              ajuda={
                arquivos.length === 1 ? undefined : `Vale para os ${arquivos.length} arquivos.`
              }
              value={categoria}
              onChange={(e) =>
                definirCategoria(e.currentTarget.value as CategoriaDoDocumentoDoPaciente)
              }
            >
              {Object.entries(NOMES_DA_CATEGORIA).map(([chave, nome]) => (
                <option key={chave} value={chave}>
                  {nome}
                </option>
              ))}
            </CampoSelecao>
            <Campo
              id="documentos-validade"
              rotulo="Validade"
              type="date"
              value={validade}
              ajuda="Opcional. Avisa 30 dias antes de vencer."
              onChange={(e) => definirValidade(e.currentTarget.value)}
            />
          </div>
          {enviar.isError && (
            <Faixa tom="bloqueado">
              {recadoDeErro(enviar.error, { generica: 'Não foi possível enviar os documentos.' })}
            </Faixa>
          )}
          <div className="docs-cli__acoes">
            <Botao
              variante="fantasma"
              type="button"
              desabilitado={enviar.isPending}
              motivo="Enviando…"
              onClick={limpar}
            >
              Cancelar
            </Botao>
            <Botao
              variante="primario"
              type="submit"
              desabilitado={enviar.isPending}
              motivo="Enviando…"
            >
              {enviar.isPending ? 'Enviando…' : `Enviar ${arquivosNoPlural(arquivos.length)}`}
            </Botao>
          </div>
        </form>
      )}
    </>
  );
}

function ApagarDocumento({
  clienteId,
  documento,
  aoFechar,
}: {
  clienteId: string;
  documento: DocumentoDoPaciente;
  aoFechar: () => void;
}): ReactNode {
  const api = useApi();
  const cache = useQueryClient();
  const apagar = useMutation({
    mutationFn: () =>
      api!.removerDocumentoDoPaciente({ caminho: { id: clienteId, documento: documento.id } }),
    onSuccess: async () => {
      aoFechar();
      await cache.invalidateQueries({ queryKey: chaves.documentosDoPaciente(clienteId) });
    },
  });

  return (
    <Dialogo
      titulo={`Apagar ${documento.nome}?`}
      largura="estreita"
      consequencia
      aoFechar={aoFechar}
      botoes={
        <>
          <Botao variante="fantasma" onClick={aoFechar}>
            Cancelar
          </Botao>
          <Botao
            variante="perigo"
            desabilitado={apagar.isPending}
            motivo="Apagando…"
            onClick={() => apagar.mutate()}
          >
            {apagar.isPending ? 'Apagando…' : 'Apagar'}
          </Botao>
        </>
      }
    >
      <p>
        O documento sai do cadastro do paciente, e o arquivo vai para a lixeira do armazenamento.
      </p>
      {apagar.isError && (
        <Faixa tom="bloqueado">
          {recadoDeErro(apagar.error, { generica: 'Não foi possível apagar o documento.' })}
        </Faixa>
      )}
    </Dialogo>
  );
}
